import React from 'react';
import { useAuth } from '../auth/AuthProvider';
import { AccessDeniedScreen } from './AccessDeniedScreen';
import { makeStyles, tokens, Spinner } from '@fluentui/react-components';

const useStyles = makeStyles({
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: tokens.spacingVerticalM,
  },
});

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute component that wraps routes requiring authentication.
 * Handles authentication state and redirects or shows appropriate screens.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, loginInProgress, errorType, error, user } = useAuth();
  const classes = useStyles();

  // Show loading state while authentication is in progress
  if (loginInProgress) {
    return (
      <div className={classes.loadingContainer}>
        <Spinner size="extra-large" label="Authenticating..." />
      </div>
    );
  }

  // Show access denied screen if there's an access denied error
  if (errorType === 'access_denied') {
    return (
      <AccessDeniedScreen 
        error={error || undefined} 
        userEmail={user?.username || user?.name}
        applicationName="Medical Management AI"
      />
    );
  }

  // If not authenticated, show authentication prompt
  if (!isAuthenticated) {
    return (
      <div className={classes.loadingContainer}>
        <div style={{ textAlign: 'center' }}>
          <h2>Authentication Required</h2>
          <p>Please sign in using the button in the header to access this page.</p>
        </div>
      </div>
    );
  }

  // User is authenticated, render the protected content
  return <>{children}</>;
};
